// Транспорт карточек с НАСТОЯЩЕЙ записью: чтение через /api/cards/*, запись — /api/cards-write/*.
//
// ЧЕМ ЭТО ОТЛИЧАЕТСЯ ОТ source_bff.ts. Тот появился, когда запись была выключена решением
// владельца, и его `сохранить` бросает `ЗаписьНеВключена` — сегодняшнюю правду он говорил
// честно. 29.07.2026 владелец запись в СВОЮ схему разрешил (дословно: «данные, которые
// есть, — история, не исправляем; новую историю пишем правильно»), и появился слой записи
// (cards_write.py) с HTTP-ручками (cards_write_api.py). Этот файл — их клиент. Чтение он
// НЕ повторяет: все читающие методы берутся из source_bff как есть — две сборки одной
// карточки разошлись бы при первой правке.
//
// ЧТО ЗДЕСЬ ГЛАВНОЕ: ОТКАЗЫ ДОХОДЯТ ДО ПОЛЕЙ. Слой записи отвечает отказом со списком
// причин ПО ВСЕМ ПОЛЯМ СРАЗУ ({'fields': {код: что не так}}), его тексты написаны для
// человека и не пересказываются. Форма получает их классом `ОтказЗаписи` и обязана показать
// у полей. Конфликт ревизий приезжает `РевизияРазошлась` — с именем того, кто успел раньше,
// и ревизией из базы.
//
// КТО АВТОР. Никто из этого файла. Личность знает только BFF: он подставляет автора в тело
// запроса сам и затирает всё, что могло прийти из браузера. Поэтому здесь нет ни author,
// ни author_login — их отправка была бы либо бесполезной, либо попыткой представиться.

import { ApiError, Unauthorized } from '../api/client'

import { описание } from './specs'
import { ОтказЗаписи, РевизияРазошлась } from './source'
import type { ИсточникКарточек, ИтогСохранения } from './source'
import { значенияИзЯдра, источникBFF, СлояНет } from './source_bff'
import type { Значение, Значения, Состояние, Сущность } from './types'
import { этоДеньги, этоФайлы, этоЯрлык } from './values'

// ─────────────────────────── форма ответов слоя записи ───────────────────────────

interface ОтветЗаписи {
  id: string
  rev: number
  state?: string
  no?: string
  changed?: boolean
  row: { id: string; rev: number; state: string; values: Record<string, unknown> } | null
}

interface ТелоОтказа {
  error?: string
  why?: string
  fields?: Record<string, string>
  rev?: number
  who?: string | null
}

const КОРЕНЬ = '/api/cards-write'

// ─────────────────────────── значения → тело запроса ───────────────────────────

/**
 * Одно значение формы → то, что ждёт слой записи.
 *
 * Ярлык связи сводится к `{id}`: название и подсказка — это как запись ПОКАЗЫВАЮТ, слою
 * записи нужен только номер, и посылать ему сегодняшнее название значило бы ничего.
 * Деньги идут как есть ({сумма, валюта}) — это и есть его язык. Файлы не идут вовсе:
 * вложения прикладываются отдельной ручкой, а не полем карточки.
 */
function значениеДляЗаписи(v: Значение): unknown {
  if (v === undefined) return null
  if (этоЯрлык(v)) return v.id ? { id: Number(v.id) } : null
  if (этоДеньги(v)) return v
  return v
}

/**
 * Значения формы → тело запроса. Идём по ОПИСАНИЮ карточки, а не по ключам объекта:
 * в значениях живут и служебные ключи (`doc_state` — на нём держится условная
 * обязательность), а слой записи принимает только поля описания и на чужой ключ
 * ответит отказом «такого поля нет» — честным, но не про то, что человек делал.
 */
function значенияДляЗаписи(сущность: Сущность, значения: Значения): Record<string, unknown> {
  const оп = описание(сущность)
  const тело: Record<string, unknown> = {}
  for (const поле of оп.поля) {
    if (!(поле.код in значения)) continue
    if (поле.вид === 'файл') continue
    if (поле.толькоЧтение === true) continue
    const v = значения[поле.код]
    if (этоФайлы(v)) continue
    тело[поле.код] = значениеДляЗаписи(v)
  }
  return тело
}

/** Пометка запроса: по ней правка находится в change_log. Уникальна в пределах разумного. */
function пометка(): string {
  return `ui_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

// ─────────────────────────── поход в сеть ───────────────────────────

async function записать(ручка: string, тело: Record<string, unknown>): Promise<ОтветЗаписи> {
  const ответ = await fetch(`${КОРЕНЬ}/${ручка}`, {
    method: 'POST',
    credentials: 'same-origin',                  // личность знает только BFF, кука его
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(тело),
  })
  if (ответ.status === 401) throw new Unauthorized()
  const текст = await ответ.text()
  const сырое: unknown = текст ? JSON.parse(текст) : {}
  const т = (сырое ?? {}) as ТелоОтказа

  // Каждый вид отказа — своим классом: форма их разводит по-разному. Отказ по данным идёт
  // к полям, конфликт ревизий — в шапку с именем успевшего, отсутствие слоя — в объяснение
  // раздела. Свалить всё в один Error значило бы показать человеку «ошибка» три раза
  // разными словами об одном и том же.
  if (ответ.status === 400 && т.error === 'refused') {
    throw new ОтказЗаписи(String(т.why ?? 'Запись отклонена'), т.fields)
  }
  if (ответ.status === 409 && т.error === 'conflict') {
    throw new РевизияРазошлась(Number(т.rev ?? 0), String(т.why ?? ''), т.who ?? undefined)
  }
  if (ответ.status === 501 || т.error === 'no_layer') {
    throw new СлояНет(String(т.why ?? 'этого слоя в своей схеме ещё нет'))
  }
  if (ответ.status === 404 && т.error === 'not_found') {
    throw new ApiError(String(т.why ?? 'Записи нет'), 404)
  }
  if (!ответ.ok) {
    throw new ApiError(String(т.why ?? т.error ?? `HTTP ${ответ.status}`), ответ.status)
  }
  return сырое as ОтветЗаписи
}

function итог(сущность: Сущность, о: ОтветЗаписи): ИтогСохранения {
  return {
    id: String(о.id),
    ревизия: Number(о.rev),
    состояние: (о.state ?? 'draft') as Состояние,
    // Значения — из карточки, которую слой записи перечитал ПОСЛЕ записи тем же кодом,
    // что /cards/record: форма получает то, что теперь лежит в базе (телефон приведён,
    // справочник кодом), а не своё же эхо.
    значения: значенияИзЯдра(сущность, о.row?.values ?? {}),
  }
}

// ─────────────────────────── операции записи ───────────────────────────

/** Новая карточка. Возвращает итог с `id`, который выдала база. */
export async function создатьКарточку(
  сущность: Сущность,
  значения: Значения,
  состояние?: Состояние,
): Promise<ИтогСохранения> {
  const о = await записать('create', {
    entity: сущность,
    values: значенияДляЗаписи(сущность, значения),
    ...(состояние ? { state: состояние } : {}),
    request_id: пометка(),
  })
  return итог(сущность, о)
}

/** Правка с проверкой ревизии. Разошлась — `РевизияРазошлась`, а не тихая перезапись. */
export async function правитьКарточку(
  сущность: Сущность,
  id: string,
  значения: Значения,
  ревизия: number,
): Promise<ИтогСохранения> {
  const о = await записать('update', {
    entity: сущность,
    id: Number(id),
    rev: ревизия,
    values: значенияДляЗаписи(сущность, значения),
    request_id: пометка(),
  })
  return итог(сущность, о)
}

/**
 * Переход состояния. Выпуск (`issued`) идёт отдельной ручкой: там выдаётся номер, и слой
 * записи вернёт его в карточке. Аннулирование требует причину — без неё слой откажет
 * своими словами, и этот отказ форма показывает как есть.
 */
export async function сменитьСостояние(
  сущность: Сущность,
  id: string,
  новое: Состояние,
  ревизия: number,
  причина?: string,
): Promise<ИтогСохранения> {
  const о = await записать(новое === 'issued' ? 'issue' : 'state', {
    entity: сущность,
    id: Number(id),
    rev: ревизия,
    ...(новое === 'issued' ? {} : { state: новое }),
    ...(причина ? { reason: причина } : {}),
    request_id: пометка(),
  })
  return итог(сущность, о)
}

/** Удаление — отметка с обязательной причиной, не DELETE. Карточка прячется из чтения. */
export async function удалитьКарточку(
  сущность: Сущность,
  id: string,
  ревизия: number,
  причина: string,
): Promise<{ id: string; ревизия: number }> {
  const о = await записать('delete', {
    entity: сущность, id: Number(id), rev: ревизия, reason: причина, request_id: пометка(),
  })
  return { id: String(о.id), ревизия: Number(о.rev) }
}

/** Снять отметку удаления. Причина обязательна так же, как у удаления. */
export async function вернутьКарточку(
  сущность: Сущность,
  id: string,
  ревизия: number,
  причина: string,
): Promise<ИтогСохранения> {
  const о = await записать('restore', {
    entity: сущность, id: Number(id), rev: ревизия, reason: причина, request_id: пометка(),
  })
  return итог(сущность, о)
}

// ─────────────────────────── источник целиком ───────────────────────────

/**
 * Источник карточек с настоящей записью: чтение — методы source_bff как есть, запись —
 * ручки /api/cards-write/*.
 *
 * `сохранить` делает ДВА шага, когда просят и правку, и переход состояния: сначала
 * update (слой записи сам решит, изменилось ли что-то), потом переход с уже новой
 * ревизией. Если правка прошла, а переход отказал — человек видит отказ перехода, но
 * правка УЖЕ в базе, и это правда, которую форма покажет перечитанными значениями.
 */
export function источникСЗаписью(): ИсточникКарточек {
  const чтение = источникBFF()
  return {
    ...чтение,

    async сохранить(
      сущность: Сущность,
      id: string,
      значения: Значения,
      ревизия: number,
      состояние?: Состояние,
    ): Promise<ИтогСохранения> {
      if (id === 'new') {
        // Новая карточка. Состояние не передаём: документ рождается черновиком, а
        // справочная запись — сразу в работе, и это решает слой записи, а не форма.
        return создатьКарточку(сущность, значения)
      }
      let текущий = await правитьКарточку(сущность, id, значения, ревизия)
      if (состояние && состояние !== текущий.состояние) {
        текущий = await сменитьСостояние(сущность, id, состояние, текущий.ревизия)
      }
      return текущий
    },
  }
}

/** Один источник на приложение: состояния у него нет, копии ни к чему. */
export const источникКарточек: ИсточникКарточек = источникСЗаписью()
